import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { get, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import {
  FormProvider,
  Iconify,
  RHFTextField,
  RHFDatePicker,
  RHFUploadAvatar,
} from '@app/components';
import { IAddProjectSchema } from '../types';
import { addProjectSchema } from '../validators';
import { useDialog, useRequestState } from '@app/hooks';
import { AddClient } from '@app/modules/clients/view/add-client';
import { addProject, updateProject } from '../slice';
import { selectAddProjectState, selectUpdateProjectState } from '../selector';
import {
  selectEmployeesState,
  getEmployees,
  getEmployeesInternsSearch,
  selectSearchEmployeesInternsState,
} from '@app/modules/employees';
import { getClients } from '@app/modules/clients/slice';
import { selectClientState } from '@app/modules/clients/selector';

const Priority = [
  { id: 1, name: 'High' },
  { id: 2, name: 'Medium' },
  { id: 3, name: 'Low' },
];

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IAddProjectSchema;
}

const AddProject = ({ open, onClose, row }: Props) => {
  const addClientDialog = useDialog();
  const dispatch = useDispatch();

  const methods = useForm<IAddProjectSchema>({
    resolver: yupResolver(addProjectSchema) as any,
    defaultValues: {
      projectName: get(row, 'name', ''),
      client: get(row, 'client', ''),
      priority: get(row, 'priority', ''),
      description: get(row, 'description', ''),
      startDate: get(row, 'startDate', ''),
      estimatedEndDate: get(row, 'endDate', ''),
      members: row ? row?.members : [],
      managers: row ? row?.managers : [],
      projectImage: row?.image,
    },
  });

  const { loading } = useRequestState({
    stateSelector: row ? selectUpdateProjectState : selectAddProjectState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });

  const onSubmit = (form: IAddProjectSchema) => {
    const managers = form.managers.map((manager: any) => manager.id);
    row
      ? dispatch(updateProject({ ...form, id: row.id, managers }))
      : dispatch(addProject({ ...form, managers }));
  };

  useEffect(() => {
    dispatch(getEmployees());
    dispatch(getClients());
    dispatch(getEmployeesInternsSearch());
  }, [dispatch]);

  const employeeState = useSelector(selectEmployeesState);
  const managerData = (employeeState.data?.records ?? [])
    .filter((option) => option.roleId === 1 || option.roleId === 2)
    .map((option) => ({ label: option.firstName, value: option.id }));

  const clientState = useSelector(selectClientState);

  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (file) {
        methods.setValue('projectImage', newFile);
        setIsImageUploaded(true);
      }
    },
    [methods]
  );
  const { data: EmployeeIntern } = useRequestState({
    stateSelector: selectSearchEmployeesInternsState,
  });

  const selectedMembers = methods.watch('members') || [];
  const selectedManagers = methods.watch('managers') || [];

  useEffect(() => {
    if (row?.members && EmployeeIntern?.users?.length > 0) {
      const initialMembers = row?.members
        .map((memberId) => {
          const member = EmployeeIntern?.users?.find((user: any) => user.id === memberId);
          return member ? { label: member?.firstName, id: member?.id } : null;
        })
        .filter(Boolean);

      methods.setValue(
        'members',
        initialMembers.map((member: any) => member.id)
      );
    }

    if (row?.managers && (employeeState?.data?.records?.length ?? 0) > 0) {
      const managers = row?.managers
        ?.map((managerId) => {
          const manager = employeeState?.data?.records?.find((emp: any) => emp?.id === managerId);
          return manager ? { id: manager?.id, name: manager?.firstName } : null;
        })
        ?.filter((m): m is { id: number; name: string } => m !== null);
      methods.setValue('managers', managers);
    }
  }, [row, EmployeeIntern, employeeState?.data?.records, methods]);

  const getManagerValues = () => {
    if (!selectedManagers || selectedManagers.length === 0) return [];

    return selectedManagers
      .map((manager) => {
        const managerId = typeof manager === 'object' ? manager.id : manager;
        const managerName =
          typeof manager === 'object'
            ? manager.name
            : managerData.find((m) => m.value === managerId)?.label ?? '';

        return {
          label: managerName,
          value: managerId,
        };
      })
      .filter((manager) => manager.label);
  };

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open}>
          <Header
            variant="dialog"
            title={row ? Strings.button.updateProject : Strings.field.addNew}
            onClose={onClose}
          />
          <Divider />
          <DialogContent>
            <Stack my={2}>
              <RHFUploadAvatar
                name={'projectImage'}
                onDrop={handleDrop}
                sx={{
                  border:
                    isImageUploaded || row?.projectImage
                      ? 'none'
                      : '5px dashed rgba(0, 0, 0, 0.08)',
                }}
              />
            </Stack>
            <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
              <Stack flex={1}>
                <RHFTextField
                  name="projectName"
                  label={Strings.field.projectName}
                  fullWidth
                  variant="outlined"
                  required
                />
              </Stack>
              <Stack flex={1} direction={'row'} gap={1}>
                <RHFTextField select label={Strings.field.client} name="client" fullWidth required>
                  {clientState.data?.map((client: any) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.firstName}
                    </MenuItem>
                  ))}
                </RHFTextField>
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{
                    borderRadius: '8px',
                    width: '62px',
                    height: '55px',
                    minWidth: '40px',
                  }}
                  onClick={addClientDialog.show}
                >
                  <Iconify icon="ic:outline-plus" />
                </Button>
              </Stack>
            </Stack>
            <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2}>
              <Stack flex={1}>
                <RHFDatePicker
                  name="startDate"
                  label={Strings.field.startDate}
                  defaultValue={row ? dayjs(row.startDate, 'DD/MM/YYYY') : null}
                  required
                />
              </Stack>
              <Stack flex={1}>
                <RHFDatePicker
                  name="estimatedEndDate"
                  label={Strings.field.estimatedEndDate}
                  defaultValue={row ? dayjs(row.endDate, 'DD/MM/YYYY') : null}
                  required
                />
              </Stack>
            </Stack>
            <Stack my={2} justifyContent={'space-between'} direction={'row'} spacing={2} flex={1}>
              <RHFTextField select name="priority" label={Strings.field.priority} required>
                {Priority.map((type) => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </RHFTextField>
            </Stack>
            <Stack my={2} direction={'row'} spacing={2}>
              <Autocomplete
                sx={{ width: '50%' }}
                multiple
                options={managerData}
                value={getManagerValues()}
                onChange={(event, newValue) => {
                  const managers = newValue.map((option) => ({
                    id: option.value,
                    name: option.label,
                  }));
                  methods.setValue('managers', managers);
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <RHFTextField
                    {...params}
                    name="managers"
                    label={Strings.field.manager}
                    fullWidth
                    required
                  />
                )}
              />
              <Autocomplete
                sx={{ width: '50%' }}
                value={
                  selectedMembers.map((member) => ({
                    label:
                      EmployeeIntern?.users?.find((user: any) => user.id === member)?.firstName ||
                      '',
                    id: member,
                  })) ?? []
                }
                multiple
                options={
                  EmployeeIntern?.users
                    ?.filter((user: any) => !selectedMembers.includes(user.id))
                    .map((user: any) => ({
                      label: user.firstName,
                      id: user.id,
                    })) ?? []
                }
                onChange={(event, value: any) => {
                  const ids = value.map((option: any) => option.id);
                  methods.setValue('members', ids);
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params: any) => (
                  <RHFTextField
                    {...params}
                    name="members"
                    label={Strings.field.member}
                    fullWidth
                    required
                  />
                )}
              />
            </Stack>
            <RHFTextField
              name="description"
              label={Strings.field.description}
              required
              fullWidth
              variant="outlined"
              multiline
              minRows={5}
            />
          </DialogContent>

          <Divider sx={{ pt: 2 }} />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              loading={loading}
              variant="contained"
              color="primary"
              type="submit"
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
      {addClientDialog.visible && (
        <AddClient open={addClientDialog.visible} onClose={addClientDialog.hide} />
      )}
    </>
  );
};

export default AddProject;
